import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MagicLinkSignIn from './MagicLinkSignIn';
import PassphraseSetup from './PassphraseSetup';
import PassphraseUnlock from './PassphraseUnlock';

const AuthModal = ({ isOpen, onClose }) => {
  const { sendMagicLink, setPassphrase, unlockWithPassphrase, user, isUnlocked, loading } = useAuth();
  const [step, setStep] = useState('magic-link'); // 'magic-link', 'passphrase-setup', 'passphrase-unlock'
  const [email, setEmail] = useState('');
  const [checkingInit, setCheckingInit] = useState(false);

  // Reset step when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCheckingInit(false);
      return;
    }

    const determineStep = async () => {
      if (!user) {
        // Not signed in
        setStep('magic-link');
        return;
      }
      
      if (isUnlocked) {
        // User is signed in and unlocked
        onClose();
        return;
      }
      
      // User is signed in but vault is not unlocked
      // Check if vault needs initialization
      setCheckingInit(true);
      try {
        const { isVaultInitialized } = await import('../../services/auth-service');
        const initialized = await isVaultInitialized(user.id);
        
        if (initialized) {
          // Vault is initialized, show unlock
          setStep('passphrase-unlock');
        } else {
          // Vault not initialized, show setup
          setStep('passphrase-setup');
        }
      } catch (error) {
        console.error('Error checking vault initialization:', error);
        // Default to unlock on error (will show error message if wrong)
        setStep('passphrase-unlock');
      } finally {
        setCheckingInit(false);
      }
    };

    determineStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id, isUnlocked]);

  // If already unlocked, close modal
  useEffect(() => {
    if (user && isUnlocked && isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isUnlocked]);

  if (!isOpen) return null;

  const handleMagicLinkAction = async (action, ...args) => {
    if (action === 'sendMagicLink') {
      const emailArg = args[0];
      setEmail(emailArg);
      const result = await sendMagicLink(emailArg);
      return result;
    }
  };

  const handlePassphraseSetup = async (passphrase) => {
    const result = await setPassphrase(passphrase);
    if (result?.success) {
      // Vault is now unlocked
      onClose();
    }
    return result;
  };

  const handlePassphraseUnlock = async (passphrase) => {
    const result = await unlockWithPassphrase(passphrase);
    if (result?.success) {
      // Vault is now unlocked
      onClose();
    } else if (result?.error?.includes('re-initialization') || result?.error?.includes('Vault salt not found')) {
      // Vault not initialized, switch to setup
      setStep('passphrase-setup');
      return { ...result, needsSetup: true };
    }
    return result;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Only close if clicking backdrop, not the modal content
          // Don't allow closing during auth flow
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#1a1a1a',
          borderRadius: '12px',
          padding: '0',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(loading && step === 'magic-link') || checkingInit ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#fff' }}>
            <p>{checkingInit ? 'Checking vault status...' : 'Loading...'}</p>
          </div>
        ) : step === 'magic-link' ? (
          <MagicLinkSignIn onSuccess={handleMagicLinkAction} />
        ) : step === 'passphrase-setup' ? (
          <PassphraseSetup onSuccess={handlePassphraseSetup} />
        ) : step === 'passphrase-unlock' ? (
          <PassphraseUnlock onSuccess={handlePassphraseUnlock} />
        ) : null}
      </div>
    </div>
  );
};

export default AuthModal;
